import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { money, num } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import ClientLinkPanel from "@/components/ClientLinkPanel";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Sparkles, FileText, Check, X, AlertCircle, ExternalLink, Loader2, Pencil, Lock, Unlock, Save, Download, Settings2, RotateCcw, Upload } from "lucide-react";
import { ProvidedView, ProvidedActions, SourceSwitch, GenerateChoiceDialog, UploadProposalDialog } from "@/components/proposal/ProvidedProposal";
import SpecProposal from "@/components/proposal/SpecProposal";

const TEXT_SECTIONS = [
  { key: "executive_summary", title: "Executive summary" },
  { key: "project_understanding", title: "Project understanding" },
  { key: "schedule_narrative", title: "Schedule" },
  { key: "staffing_narrative", title: "Staffing" },
  { key: "safety_quality", title: "Safety & quality" },
  { key: "closing", title: "Closing" },
];

// W8 — Procore-style Detail Settings. Grouped booleans + pricing presentation controls.
const SETTING_GROUPS = [
  { title: "Quick simplification", keys: [["lump_sum", "Lump sum (single total)"], ["groups_only", "Groups only (collapse lines)"]] },
  { title: "Cover & contacts", keys: [["customer_block", "Customer information"], ["estimator_block", "Prepared by (company & facts)"]] },
  { title: "Narrative", keys: [["executive_summary", "Executive summary"], ["project_understanding", "Project understanding"], ["execution_plan", "Execution plan (per phase)"], ["execution_strategy", "Execution strategy"], ["schedule_narrative", "Schedule notes"], ["staffing_narrative", "Staffing narrative"], ["safety_quality", "Safety & quality"], ["bid_confidence", "Bid confidence"], ["closing", "Closing / thank-you"]] },
  { title: "Tables & sections", keys: [["glance", "Project at a glance"], ["unit_basis", "Unit price basis"], ["build_pace", "Build pace"], ["schedule_table", "Construction schedule"], ["man_loaded_table", "Man-loaded schedule"], ["milestones", "Milestones"], ["alternates", "Alternates"], ["bond", "Performance bond"], ["warranty", "Extended warranty"], ["inclusions", "Inclusions"], ["exclusions", "Exclusions"], ["clarifications", "Clarifications"], ["schedule_baseline", "Schedule vs baseline"], ["acceptance", "Acceptance page"]] },
  { title: "Charts & figures", keys: [["fig_scope_mh", "Fig 1 · Man-hours by scope"], ["fig_price_by_group", "Fig 2 · Price by scope"], ["fig_gantt", "Fig 3 · Execution gantt"], ["fig_man_days", "Fig 4 · Man-days"], ["fig_monthly_manpower", "Fig 5 · Monthly manpower"], ["fig_cash_flow", "Fig 6 · Cash flow"], ["fig_production", "Fig 7 · Daily production"], ["fig_equipment_share", "Fig 8 · Equipment share"], ["fig_equipment_monthly", "Fig 9 · Equipment monthly"], ["fig_equipment_peak", "Fig 10 · Peak equipment"], ["fig_weather", "Fig 11 · Weather calendar"]] },
];
const ALL_SETTING_KEYS = SETTING_GROUPS.flatMap((g) => g.keys.map((k) => k[0]));

export default function ProposalTab({ bid, perms = {}, locked: bidSent }) {
  const navigate = useNavigate();
  const [doc, setDoc] = useState(null);
  const [status, setStatus] = useState("none");
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);
  const [info, setInfo] = useState(null);
  const [view, setView] = useState(null);
  const [showUpload, setShowUpload] = useState(false);
  const [genChoice, setGenChoice] = useState(false);
  const [expiredFacts, setExpiredFacts] = useState([]);
  const poll = useRef(null);
  useEffect(() => { api.get("/company/facts").then((r) => setExpiredFacts(r.data?.expired || [])).catch(() => {}); }, []);

  const canEdit = !!perms.canPrice && !bidSent;
  const canUnlock = !!perms.canSetMargin;
  const source = info?.source;
  const hasProvided = !!info?.provided;
  const shown = view || source || "generated";
  const showProvided = shown === "provided" && hasProvided;
  const genLocked = !!doc?.locked;
  const isLocked = showProvided ? !!info?.provided?.locked : genLocked;
  const canUpload = canEdit && !(hasProvided && info.provided.locked);

  const fetchDoc = async () => {
    try {
      const r = await api.get(`/bids/${bid.bid_id}/proposal`);
      setStatus(r.data.status);
      setInfo(r.data);
      if (r.data.exists) setDoc(r.data);
      if (r.data.settings) setSettings(r.data.settings);
      return r.data.status;
    } catch {
      setStatus("none");
      return "none";
    }
  };

  useEffect(() => {
    fetchDoc();
    return () => poll.current && clearInterval(poll.current);
    // eslint-disable-next-line
  }, [bid.bid_id]);

  const generate = async (makeMain = false) => {
    setGenChoice(false);
    try {
      await api.post(`/bids/${bid.bid_id}/proposal/generate`, { make_main: !!makeMain });
      setStatus("generating");
      if (makeMain) setView("generated");
      toast.info("Drafting the proposal from the estimate…");
      poll.current && clearInterval(poll.current);
      poll.current = setInterval(async () => {
        const s = await fetchDoc();
        if (s === "ready") { clearInterval(poll.current); toast.success("Proposal ready"); }
        else if (s === "failed") { clearInterval(poll.current); toast.error("Generation failed — please try again."); }
      }, 4000);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start generation");
    }
  };

  const startEdit = () => {
    const s = doc.sections || {};
    setDraft({
      sections: {
        ...TEXT_SECTIONS.reduce((a, x) => ({ ...a, [x.key]: s[x.key] || "" }), {}),
        execution_plan: (Array.isArray(s.execution_plan) ? s.execution_plan : []).map((p) => ({ ...p })),
      },
      inclusions: (doc.inclusions || []).join("\n"),
      exclusions: (doc.exclusions || []).join("\n"),
    });
    setEditing(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put(`/bids/${bid.bid_id}/proposal`, {
        sections: draft.sections,
        inclusions: draft.inclusions.split("\n").map((x) => x.trim()).filter(Boolean),
        exclusions: draft.exclusions.split("\n").map((x) => x.trim()).filter(Boolean),
      });
      setDoc(r.data);
      setEditing(false);
      toast.success("Proposal saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save");
    } finally { setSaving(false); }
  };

  const lock = async () => {
    try { await api.post(`/bids/${bid.bid_id}/proposal/lock`); await fetchDoc(); toast.success("Proposal locked"); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not lock"); }
  };
  const unlock = async () => {
    try { await api.post(`/bids/${bid.bid_id}/proposal/unlock`); await fetchDoc(); toast.success("Proposal unlocked"); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not unlock"); }
  };
  const makeMain = async (src) => {
    try { await api.post(`/bids/${bid.bid_id}/proposal/source`, { source: src }); await fetchDoc(); setView(null); toast.success("Main proposal changed"); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not switch"); }
  };

  const saveSettings = async (next) => {
    setSettings(next);
    setSavingSettings(true);
    try { await api.put(`/bids/${bid.bid_id}`, { proposal_settings: next }); }
    catch { toast.error("Could not save detail settings"); }
    finally { setSavingSettings(false); }
  };

  const generating = status === "generating";

  // Hand-over 2F: unless the legacy generator is switched on in Settings, the Proposal tab is the spec-driven document.
  // An uploaded PDF made the main proposal keeps the provided view (switch it back to generated to return here).
  if (info && info.engine !== "legacy" && source !== "provided") {
    return (
      <>
        <SpecProposal bid={bid} perms={perms} locked={bidSent} onUpload={canUpload ? () => setShowUpload(true) : null} />
        {showUpload && <UploadProposalDialog bid={bid} open={showUpload} onOpenChange={setShowUpload} hasGenerated={!!info.spec_exists} onDone={() => { setView(null); fetchDoc(); }} />}
      </>
    );
  }

  return (
    <div className="p-6 max-w-[1800px] w-full mx-auto">
      <ExpiredFactsBanner facts={expiredFacts} />
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div>
          <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-600">Proposal</h2>
          <p className="text-xs text-slate-500 mt-0.5">{source === "provided" ? "The uploaded PDF is the main proposal: it's what prints, shares and sends. Lock it once it's final." : "Adaptive document written from the estimate, or upload your own PDF. Edit the narrative, then lock it so the locked version is exactly what prints."}</p>
          {bid.proposal_drive?.web_link && (
            <a href={bid.proposal_drive.web_link} target="_blank" rel="noreferrer" data-testid="proposal-drive-link"
              className="inline-flex items-center gap-1 mt-1 text-xs text-emerald-700 hover:text-emerald-900">
              <Check className="w-3.5 h-3.5" /> Filed to Google Drive → To Client{bid.proposal_drive.revision_label ? ` (${bid.proposal_drive.revision_label})` : ""}
            </a>
          )}
        </div>
        <div className="flex gap-2 flex-wrap">
          {showProvided && !editing && <ProvidedActions bid={bid} provided={info.provided} />}
          {!showProvided && doc && !editing && (
            <Button variant="outline" onClick={() => navigate(`/bids/${bid.bid_id}/proposal/print`)} data-testid="proposal-open-print">
              <ExternalLink className="w-4 h-4 mr-1.5" /> Open / Download PDF
            </Button>
          )}
          {!showProvided && doc && !editing && (
            <Button variant="outline" onClick={async () => {
              try {
                const res = await api.get(`/bids/${bid.bid_id}/programme/export`, { responseType: "blob" });
                const url = URL.createObjectURL(res.data); const a = document.createElement("a");
                a.href = url; a.download = "estimate_programme.xlsx"; a.click(); URL.revokeObjectURL(url);
              } catch { toast.error("Could not export programme"); }
            }} data-testid="proposal-programme-export">
              <Download className="w-4 h-4 mr-1.5" /> Programme (Excel)
            </Button>
          )}
          {!showProvided && doc && !editing && (
            <Button variant="outline" onClick={() => setShowSettings((v) => !v)} data-testid="proposal-detail-settings-button">
              <Settings2 className="w-4 h-4 mr-1.5" /> Detail settings
            </Button>
          )}
          {!showProvided && doc && !editing && canEdit && !genLocked && (
            <Button variant="outline" onClick={startEdit} data-testid="proposal-edit-button">
              <Pencil className="w-4 h-4 mr-1.5" /> Edit narrative
            </Button>
          )}
          {editing && (
            <>
              <Button variant="outline" onClick={() => setEditing(false)} data-testid="proposal-cancel-edit">Cancel</Button>
              <Button onClick={save} disabled={saving} className="bg-[#003E87] hover:bg-[#00305F]" data-testid="proposal-save-button">
                {saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Save className="w-4 h-4 mr-1.5" />} Save
              </Button>
            </>
          )}
          {(showProvided || doc) && !editing && canEdit && shown === source && (
            isLocked
              ? (canUnlock && <Button variant="outline" onClick={unlock} data-testid="proposal-unlock-button"><Unlock className="w-4 h-4 mr-1.5" /> Unlock</Button>)
              : <Button variant="outline" onClick={lock} data-testid="proposal-lock-button"><Lock className="w-4 h-4 mr-1.5" /> Lock</Button>
          )}
          {!editing && canUpload && (
            <Button variant="outline" onClick={() => setShowUpload(true)} data-testid="proposal-upload-button">
              <Upload className="w-4 h-4 mr-1.5" /> {hasProvided ? "Upload new version" : "Upload proposal"}
            </Button>
          )}
          {!editing && !genLocked && (
            <Button onClick={() => (hasProvided && source === "provided" ? setGenChoice(true) : generate(false))} disabled={generating} className="bg-[#F4C400] text-black hover:brightness-95" data-testid="proposal-generate-pdf-button">
              {generating ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Sparkles className="w-4 h-4 mr-1.5" />}
              {generating ? "Generating…" : doc ? "Regenerate" : "Generate proposal"}
            </Button>
          )}
        </div>
      </div>

      {isLocked && !editing && (
        <div className="mb-4 rounded-md border border-slate-800 bg-slate-900 text-white text-xs px-4 py-2.5 flex items-center gap-2" data-testid="proposal-locked-banner">
          <Lock className="w-3.5 h-3.5" /> Locked{(showProvided ? info.provided : doc).locked_by ? ` by ${(showProvided ? info.provided : doc).locked_by}` : ""}. This is exactly what prints. {canUnlock ? "Unlock to edit or regenerate." : "Ask an owner or chief estimator to unlock."}
        </div>
      )}

      {hasProvided && doc && !editing && (
        <SourceSwitch info={info} view={shown} setView={setView} canSwitch={canEdit} onMakeMain={makeMain} />
      )}

      {(doc || hasProvided) && !generating && !editing && <ClientLinkPanel bid={bid} canManage={!!perms.canSend} />}

      {generating && !showProvided && (
        <Card className="p-8 flex flex-col items-center text-center gap-3" data-testid="proposal-generating">
          <Loader2 className="w-6 h-6 text-[#003E87] animate-spin" />
          <div className="text-sm text-slate-600">Drafting scope, execution plan, schedule and staffing narratives… this takes up to a couple of minutes.</div>
        </Card>
      )}

      {!doc && !generating && !hasProvided && (
        <Card className="p-12 flex flex-col items-center text-center gap-3">
          <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center"><FileText className="w-5 h-5 text-slate-400" /></div>
          <div className="text-sm text-slate-500">Generate a client-ready proposal — cover, executive summary, project understanding, execution plan, schedule, staffing, equipment, inclusions/exclusions and firm-fixed pricing. Or upload a proposal you built yourself.</div>
        </Card>
      )}

      {showProvided && <ProvidedView info={info} />}
      {!showProvided && doc && !generating && !editing && showSettings && settings && (
        <ProposalSettings settings={settings} saving={savingSettings} onChange={saveSettings} />
      )}
      {!showProvided && doc && !generating && !editing && <PreviewDoc doc={doc} settings={settings} />}
      {!showProvided && doc && editing && <EditDoc doc={doc} draft={draft} setDraft={setDraft} />}
      <GenerateChoiceDialog open={genChoice} onOpenChange={setGenChoice} onChoose={generate} />
      {showUpload && <UploadProposalDialog bid={bid} open={showUpload} onOpenChange={setShowUpload} hasGenerated={!!doc} onDone={() => { setView(null); fetchDoc(); }} />}
    </div>
  );
}

function EditDoc({ doc, draft, setDraft }) {
  const setSec = (k, v) => setDraft((d) => ({ ...d, sections: { ...d.sections, [k]: v } }));
  const setPhase = (i, v) => setDraft((d) => {
    const ep = [...(d.sections.execution_plan || [])]; ep[i] = { ...ep[i], narrative: v };
    return { ...d, sections: { ...d.sections, execution_plan: ep } };
  });
  return (
    <Card className="p-8 space-y-6" data-testid="proposal-editor">
      {TEXT_SECTIONS.map((s) => (
        <div key={s.key}>
          <label className="text-sm font-semibold text-slate-800 mb-1.5 block">{s.title}</label>
          <Textarea value={draft.sections[s.key]} onChange={(e) => setSec(s.key, e.target.value)}
            rows={s.key === "executive_summary" || s.key === "project_understanding" ? 5 : 3}
            data-testid={`proposal-edit-${s.key}`} className="text-sm" />
        </div>
      ))}
      {(draft.sections.execution_plan || []).length > 0 && (
        <div>
          <label className="text-sm font-semibold text-slate-800 mb-1.5 block">Execution plan</label>
          <div className="space-y-3">
            {draft.sections.execution_plan.map((p, i) => (
              <div key={i}>
                <div className="text-xs font-medium text-slate-600 mb-1">{p.phase}</div>
                <Textarea value={p.narrative} onChange={(e) => setPhase(i, e.target.value)} rows={2} className="text-sm" data-testid={`proposal-edit-phase-${i}`} />
              </div>
            ))}
          </div>
        </div>
      )}
      <div className="grid sm:grid-cols-2 gap-6">
        <div>
          <label className="text-sm font-semibold text-slate-800 mb-1.5 block">Inclusions <span className="text-xs text-slate-400 font-normal">(one per line)</span></label>
          <Textarea value={draft.inclusions} onChange={(e) => setDraft((d) => ({ ...d, inclusions: e.target.value }))} rows={6} className="text-sm" data-testid="proposal-edit-inclusions" />
        </div>
        <div>
          <label className="text-sm font-semibold text-slate-800 mb-1.5 block">Exclusions <span className="text-xs text-slate-400 font-normal">(one per line)</span></label>
          <Textarea value={draft.exclusions} onChange={(e) => setDraft((d) => ({ ...d, exclusions: e.target.value }))} rows={6} className="text-sm" data-testid="proposal-edit-exclusions" />
        </div>
      </div>
    </Card>
  );
}

function PreviewDoc({ doc, settings }) {
  const s = doc.sections || {};
  const on = (k) => (settings ? !!settings[k] : true);
  return (
    <Card className="p-8" data-testid="proposal-document">
      <div className="flex items-start justify-between border-b border-slate-100 pb-5 mb-5">
        <div>
          <div className="font-display text-2xl font-bold text-slate-900">{doc.name}</div>
          <div className="text-sm text-slate-500 mt-1">Prepared for {doc.client_name} · {doc.location} · {num(doc.capacity_mw)} MW</div>
        </div>
        <div className="text-right">
          <div className="text-[11px] uppercase tracking-wide text-slate-400">Firm fixed price</div>
          <div className="font-display text-2xl font-bold text-slate-900">{money(doc.totals?.total)}</div>
          <div className="text-xs text-slate-500 font-mono">{doc.totals?.cost_per_watt ? `$${doc.totals.cost_per_watt.toFixed(4)}/W` : ""}</div>
        </div>
      </div>

      {doc.warning && (
        <div className="mb-5 rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800 flex items-center gap-2">
          <AlertCircle className="w-4 h-4" /> {doc.warning}
        </div>
      )}
      <div className="mb-4 flex items-center gap-2">
        {doc.ai_used ? <Badge className="bg-indigo-50 text-indigo-700 hover:bg-indigo-50 text-[10px]">AI-drafted narrative</Badge> : <Badge variant="outline" className="text-[10px]">Data-only</Badge>}
        {doc.edited && <Badge variant="outline" className="text-[10px]">Edited</Badge>}
        {doc.locked && <Badge className="bg-slate-900 text-white hover:bg-slate-900 text-[10px]">Locked</Badge>}
      </div>

      {on("executive_summary") && <Sec title="Executive summary" body={s.executive_summary} />}
      {on("project_understanding") && <Sec title="Project understanding" body={s.project_understanding} />}
      {on("execution_plan") && Array.isArray(s.execution_plan) && s.execution_plan.length > 0 && (
        <div className="mb-6">
          <h3 className="text-sm font-semibold text-slate-800 mb-2">Execution plan</h3>
          <div className="space-y-3">
            {s.execution_plan.map((p, i) => (
              <div key={i}><div className="text-sm font-medium text-slate-700">{p.phase}</div><div className="text-sm text-slate-600 leading-relaxed">{p.narrative}</div></div>
            ))}
          </div>
        </div>
      )}
      {on("schedule_narrative") && <Sec title="Schedule" body={s.schedule_narrative} />}
      {on("schedule_baseline") && doc.figures?.schedule_baseline && (
        <div className="mb-6" data-testid="proposal-schedule-baseline">
          <h3 className="text-sm font-semibold text-slate-800 mb-2">Schedule vs baseline</h3>
          <p className="text-sm text-slate-600 mb-2">Baseline finish <b>{doc.figures.schedule_baseline.baseline_end}</b> · current finish <b>{doc.figures.schedule_baseline.current_end}</b>
            {doc.figures.schedule_baseline.end_slip != null && <span className={doc.figures.schedule_baseline.end_slip > 0 ? "text-rose-600 font-semibold" : "text-emerald-600 font-semibold"}> {doc.figures.schedule_baseline.end_slip > 0 ? `+${doc.figures.schedule_baseline.end_slip}d late` : doc.figures.schedule_baseline.end_slip < 0 ? `${-doc.figures.schedule_baseline.end_slip}d early` : "on plan"}</span>}
          </p>
          {doc.figures.schedule_baseline.rows?.length > 0 && (
            <table className="w-full text-sm"><thead><tr className="text-[11px] uppercase tracking-wide text-slate-400 border-b border-slate-100"><th className="text-left font-medium py-1.5">Scope</th><th className="text-left font-medium">Baseline finish</th><th className="text-left font-medium">Current finish</th><th className="text-right font-medium">Slip</th></tr></thead>
              <tbody>{doc.figures.schedule_baseline.rows.map((r, i) => (
                <tr key={i} className="border-b border-slate-50"><td className="py-1.5 text-slate-800">{r.scope}</td><td className="text-slate-500">{r.baseline_end}</td><td className="text-slate-500">{r.current_end}</td><td className={`text-right font-mono ${r.slip > 0 ? "text-rose-600" : r.slip < 0 ? "text-emerald-600" : "text-slate-400"}`}>{r.slip > 0 ? `+${r.slip}d` : r.slip < 0 ? `${r.slip}d` : "0"}</td></tr>
              ))}</tbody></table>
          )}
        </div>
      )}
      {on("staffing_narrative") && <Sec title="Staffing" body={s.staffing_narrative} />}
      {on("safety_quality") && <Sec title="Safety & quality" body={s.safety_quality} />}

      <div className="mb-6">
        <h3 className="text-sm font-semibold text-slate-800 mb-2">Scope of supply — pricing</h3>
        <table className="w-full text-sm">
          <thead><tr className="text-[11px] uppercase tracking-wide text-slate-400 border-b border-slate-100">
            <th className="text-left font-medium py-1.5">Item</th><th className="text-left font-medium">Discipline</th>
            <th className="text-right font-medium">Quantity</th><th className="text-right font-medium">Amount</th></tr></thead>
          <tbody>
            {(doc.pricing_rows || []).map((r, i) => (
              <tr key={i} className="border-b border-slate-50"><td className="py-2 text-slate-800">{r.name}</td>
                <td className="capitalize text-slate-500">{r.discipline}</td>
                <td className="text-right font-mono">{num(r.quantity)} {r.unit}</td>
                <td className="text-right font-mono text-slate-800">{money(r.subtotal)}</td></tr>
            ))}
            <tr className="font-semibold"><td className="py-2" colSpan={3}>Total (firm fixed price)</td><td className="text-right font-mono">{money(doc.totals?.total)}</td></tr>
          </tbody>
        </table>
      </div>

      {on("allowances") && (doc.totals?.allowances?.length > 0) && (
        <div className="mb-6" data-testid="proposal-allowances">
          <h3 className="text-sm font-semibold text-slate-800 mb-2">Allowances included</h3>
          <p className="text-xs text-slate-500 mb-2">The firm fixed price above includes the following budgeted allowances for scope to be finalized; actual costs are reconciled against these amounts.</p>
          <table className="w-full text-sm"><tbody>
            {doc.totals.allowances.map((a, i) => (
              <tr key={i} className="border-b border-slate-50"><td className="py-1.5 text-slate-800">{a.name}{a.cost_code ? <span className="text-slate-400 ml-1">· {a.cost_code}</span> : null}{a.detail ? <span className="block text-[11px] text-slate-400 font-mono">{a.detail}</span> : null}</td><td className="text-right font-mono text-slate-800">{money(a.amount)}</td></tr>
            ))}
            <tr className="font-semibold"><td className="py-1.5">Total allowances</td><td className="text-right font-mono">{money(doc.totals.allowances_total)}</td></tr>
          </tbody></table>
        </div>
      )}

      {on("alternates") && ((doc.totals?.alternates || []).filter((a) => a.show_on_proposal !== false).length > 0) && (
        <div className="mb-6" data-testid="proposal-alternates">
          <h3 className="text-sm font-semibold text-slate-800 mb-2">Alternates — client options</h3>
          <p className="text-xs text-slate-500 mb-2">Optional add/deduct scope, priced separately and not included in the firm fixed price unless accepted.</p>
          <table className="w-full text-sm">
            <thead><tr className="text-[11px] uppercase tracking-wide text-slate-400 border-b border-slate-100">
              <th className="text-left font-medium py-1.5">Alternate</th><th className="text-left font-medium">Type</th><th className="text-right font-medium">Amount</th></tr></thead>
            <tbody>
              {doc.totals.alternates.filter((a) => a.show_on_proposal !== false).map((a, i) => (
                <tr key={i} className="border-b border-slate-50"><td className="py-1.5 text-slate-800">{a.name}{a.bundle ? <span className="ml-1.5 text-[10px] uppercase tracking-wide text-[#003E87] bg-[#003E87]/10 rounded px-1.5 py-0.5">{a.bundle}</span> : null}{a.accepted ? <span className="text-emerald-600 ml-1 text-xs">✓ accepted</span> : null}{a.detail ? <span className="block text-[11px] text-slate-400 font-mono">{a.detail}</span> : null}</td>
                  <td className="capitalize text-slate-500">{a.type}</td>
                  <td className="text-right font-mono text-slate-800">{a.type === "deduct" ? "−" : "+"}{money(a.amount)}</td></tr>
              ))}
            </tbody>
          </table>
          {doc.totals.accepted_alternates_amount ? (
            <div className="flex justify-between text-sm font-semibold mt-2 pt-2 border-t border-slate-100"><span>Adjusted price with accepted alternates</span><span className="font-mono">{money(doc.totals.adjusted_total)}</span></div>
          ) : null}
        </div>
      )}

      {(on("inclusions") || on("exclusions")) && (
        <div className="grid sm:grid-cols-2 gap-6 mt-6">
          {on("inclusions") && <ClauseList title="Inclusions" items={doc.inclusions} icon={Check} tone="text-emerald-600" />}
          {on("exclusions") && <ClauseList title="Exclusions" items={doc.exclusions} icon={X} tone="text-rose-600" />}
        </div>
      )}
      {on("closing") && s.closing && <div className="mt-6 text-sm text-slate-600 whitespace-pre-wrap leading-relaxed border-t border-slate-100 pt-4">{s.closing}</div>}
    </Card>
  );
}

function Sec({ title, body }) {
  if (!body) return null;
  return (
    <div className="mb-6">
      <h3 className="text-sm font-semibold text-slate-800 mb-2">{title}</h3>
      <div className="text-sm text-slate-600 whitespace-pre-wrap leading-relaxed">{body}</div>
    </div>
  );
}

function ProposalSettings({ settings, onChange, saving }) {
  const set = (k, v) => onChange({ ...settings, [k]: v });
  const bulk = (val) => onChange({ ...settings, ...ALL_SETTING_KEYS.reduce((a, k) => ({ ...a, [k]: val }), {}), summary_total: true });
  return (
    <Card className="p-6 mb-4" data-testid="proposal-settings-panel">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div>
          <h3 className="text-sm font-semibold text-slate-800 flex items-center gap-1.5"><Settings2 className="w-4 h-4 text-[#003E87]" /> Detail settings</h3>
          <p className="text-xs text-slate-500 mt-0.5">Choose exactly what appears on the client proposal. A brand-new bid starts price-only; turn on what you want to show.</p>
        </div>
        <div className="flex items-center gap-3">
          {saving && <Loader2 className="w-4 h-4 animate-spin text-slate-400" />}
          <button onClick={() => bulk(true)} className="text-xs text-[#003E87] hover:underline" data-testid="proposal-settings-all-on">Everything on</button>
          <button onClick={() => bulk(false)} className="text-xs text-slate-500 hover:underline flex items-center gap-1" data-testid="proposal-settings-price-only"><RotateCcw className="w-3 h-3" /> Price only</button>
        </div>
      </div>

      <div className="rounded-lg border border-slate-100 p-3 mb-4 grid sm:grid-cols-4 gap-3" data-testid="proposal-settings-pricing">
        <label className="text-xs text-slate-600">Group by
          <select value={settings.group_by || "discipline"} onChange={(e) => set("group_by", e.target.value)} data-testid="proposal-setting-group_by" className="mt-1 w-full border border-slate-200 rounded px-2 py-1 text-sm">
            <option value="discipline">Discipline</option><option value="groups">Scope group</option><option value="cost_code">Cost code</option><option value="scope">Scope</option>
          </select>
        </label>
        <label className="text-xs text-slate-600">Decimals
          <select value={settings.round_decimals ?? 0} onChange={(e) => set("round_decimals", Number(e.target.value))} data-testid="proposal-setting-round_decimals" className="mt-1 w-full border border-slate-200 rounded px-2 py-1 text-sm">
            <option value={0}>0</option><option value={1}>1</option><option value={2}>2</option>
          </select>
        </label>
        <div className="flex flex-col gap-1.5 justify-center">
          <Toggle k="round_total" label="Round total ($1k)" settings={settings} set={set} />
          <Toggle k="unit_price_col" label="Unit price column" settings={settings} set={set} />
        </div>
        <div className="flex flex-col gap-1.5 justify-center">
          <Toggle k="show_per_watt" label="Show $/W" settings={settings} set={set} />
          <Toggle k="show_per_mw" label="Show $/MW" settings={settings} set={set} />
        </div>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {SETTING_GROUPS.map((g) => (
          <div key={g.title}>
            <div className="text-[11px] uppercase tracking-wide text-slate-400 mb-1.5">{g.title}</div>
            <div className="space-y-1">
              {g.keys.map(([k, label]) => <Toggle key={k} k={k} label={label} settings={settings} set={set} />)}
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}

function Toggle({ k, label, settings, set }) {
  const val = !!settings[k];
  return (
    <button type="button" onClick={() => set(k, !val)} data-testid={`proposal-setting-${k}`} className="w-full flex items-center gap-2 text-left">
      <span className={`relative inline-flex h-4 w-7 shrink-0 items-center rounded-full transition-colors ${val ? "bg-[#003E87]" : "bg-slate-200"}`}>
        <span className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${val ? "translate-x-3.5" : "translate-x-0.5"}`} />
      </span>
      <span className={`text-xs ${val ? "text-slate-800" : "text-slate-500"}`}>{label}</span>
    </button>
  );
}
function ClauseList({ title, items, icon: Icon, tone }) {
  return (
    <div>
      <h3 className="text-sm font-semibold text-slate-800 mb-2">{title}</h3>
      <ul className="space-y-1.5">
        {(items || []).map((t, i) => (
          <li key={i} className="flex items-start gap-2 text-sm text-slate-700"><Icon className={`w-3.5 h-3.5 mt-0.5 shrink-0 ${tone}`} /><span>{t}</span></li>
        ))}
        {(!items || items.length === 0) && <li className="text-xs text-slate-400">None</li>}
      </ul>
    </div>
  );
}

function ExpiredFactsBanner({ facts }) {
  if (!facts?.length) return null;
  return (
    <div className="mb-4 flex items-start gap-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2" data-testid="proposal-expired-facts-banner">
      <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
      <span>
        {facts.length} company fact{facts.length === 1 ? " is" : "s are"} past the expiry date and still print on this proposal:{" "}
        {facts.map((f) => `${f.label || f.key} (expired ${f.expires})`).join(", ")}. Update them in Settings, Company.
      </span>
    </div>
  );
}

